import get from "lodash/get";
import React from "react";
import { Button, Modal, PlusIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { isBefore, parse } from "date-fns";
import { useTranslation } from "react-i18next";
import { useRouteMatch } from "react-router-dom";

import QRatingForm from "~/components/Forms/QRatingForm";
import Wrapper from "~/shared/ui/Wrapper";
import {
  useGetLocationData,
  useGoToPreviousPage,
  useSetLocationData,
} from "~/hooks/locationData";
import { getIndicatorName, setDefaultSelectR } from "~/libs/getData";
import { getFormatDate } from "~/libs/getFormatDate";
import {
  useDeleteQListRatingMutation,
  useGetQRatingsQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { ConfirmModal } from "~/shared/ui/ConfirmModal";
import { TableV2 } from "~/shared/ui/TableV2";

import { getEditorQColumns } from "./model/tableAdapter";
import {
  EDITOR_MANAGING_Q_INDICATORS_OPTIONS,
  EDITOR_MANAGING_Q_INDICATORS_PERIOD_OPTIONS,
} from "./options";

const EditorQIndicators = () => {
  const { t } = useTranslation();
  const language = localStorage.getItem("language");
  const isEn = language === "en";
  const { params } = useRouteMatch();
  const { hasPermission } = usePermissions();
  const setData = useSetLocationData();
  const { openModal, openChildModal, currentId, dateBegin } =
    useGetLocationData();
  const goBack = useGoToPreviousPage();
  const { data, loading, refetch } = useGetQRatingsQuery({
    variables: {
      RatingsVersionId: params["id"],
    },
    fetchPolicy: "cache-and-network",
  });

  const idPeriod = (name) => (name ? name.period : "");

  const qRatings = get(data, "QuartilesRatingGetter.getRatings", []).map(
    ({
      KPIru,
      KPIen,
      LastPeriodIndicatorId,
      ValueIndicatorId,
      GroupIndicatorId,
      AverageIndicatorId,
      DifferenceIndicatorId,
      GroupAverageIndicatorId,
      LastPeriodIndicator,
      ValueIndicator,
      GroupIndicator,
      AverageIndicator,
      DifferenceIndicator,
      GroupAverageIndicator,
      Parent,
      PrevElement,
      updatedAt,
      ...q
    }) => ({
      ...q,
      LastPeriodIndicatorId,
      ValueIndicatorId,
      GroupIndicatorId,
      AverageIndicatorId,
      DifferenceIndicatorId,
      GroupAverageIndicatorId,
      name: isEn ? KPIen : KPIru,
      LastPeriodIndicatorIdPeriod: idPeriod(LastPeriodIndicator),
      ValueIndicatorIdPeriod: idPeriod(ValueIndicator),
      GroupIndicatorIdPeriod: idPeriod(GroupIndicator),
      AverageIndicatorIdPeriod: idPeriod(AverageIndicator),
      DifferenceIndicatorIdPeriod: idPeriod(DifferenceIndicator),
      GroupAverageIndicatorIdPeriod: idPeriod(GroupAverageIndicator),
      LastPeriodIndicatorIdOption: setDefaultSelectR(
        LastPeriodIndicatorId,
        getIndicatorName(LastPeriodIndicator),
      ),
      ValueIndicatorIdOption: setDefaultSelectR(
        ValueIndicatorId,
        getIndicatorName(ValueIndicator),
      ),
      GroupIndicatorIdOption: setDefaultSelectR(
        GroupIndicatorId,
        getIndicatorName(GroupIndicator),
      ),
      AverageIndicatorIdOption: setDefaultSelectR(
        AverageIndicatorId,
        getIndicatorName(AverageIndicator),
      ),
      DifferenceIndicatorIdOption: setDefaultSelectR(
        DifferenceIndicatorId,
        getIndicatorName(DifferenceIndicator),
      ),
      GroupAverageIndicatorIdOption: setDefaultSelectR(
        GroupAverageIndicatorId,
        getIndicatorName(GroupAverageIndicator),
      ),
      parentName: isEn ? get(Parent, "KPIen", "") : get(Parent, "KPIru", ""),
      prevElementName: isEn
        ? get(PrevElement, "KPIen", "")
        : get(PrevElement, "KPIru", ""),
      updatedAt: getFormatDate(updatedAt),
      KPIru,
      KPIen,
    }),
  );

  const selectedQ = qRatings.find((i) => i.id === currentId);

  const handleCloseModal = () => {
    setData("openModal", "", "openChildModal", "");
  };

  const isEditable = isBefore(
    new Date(),
    parse(dateBegin, "dd.MM.yyyy", new Date()),
  );

  const prevElemId = get(data, "QuartilesRatingGetter.getRatings[0].id", "");
  const openAddModal = () => {
    setData("openModal", "Add");
  };

  const [deleteListRatingMutation] = useDeleteQListRatingMutation({
    variables: { id: currentId },
    onCompleted() {
      refetch();
    },
  });
  const isAdd = openModal === "Add";
  const isEdit = openModal === "Edit";
  const isView = openChildModal === "View";

  const deleteListRating = () => {
    deleteListRatingMutation();
    handleCloseModal();
  };

  const handleRowView = (record: { id: string }) => {
    setData("openChildModal", "View", "currentId", record.id);
  };

  const handleRowEdit = (record: { id: string }) => {
    setData("openModal", "Edit", "currentId", record.id);
  };

  const handleRowDelete = (record: { id: string }) => {
    setData("openModal", "Sure", "currentId", record.id);
  };

  const formId = "QRatingForm";

  return (
    <Wrapper.Content
      title={t("editorQ.managing_indicators")}
      goBackHandler={goBack}
      txtBtn={t("ui.back")}
      controlComponent={
        <Flex gap={10}>
          {isEditable && hasPermission("quartilesRatings.rating.set") && (
            <Button
              onClick={openAddModal}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("ui.add")}
            </Button>
          )}
        </Flex>
      }
    >
      <TableV2
        columns={getEditorQColumns(
          t,
          EDITOR_MANAGING_Q_INDICATORS_OPTIONS,
          EDITOR_MANAGING_Q_INDICATORS_PERIOD_OPTIONS,
        )}
        dataSource={qRatings}
        loading={loading}
        onRowEdit={
          isEditable &&
          hasPermission("quartilesRatings.rating.set") &&
          handleRowEdit
        }
        onRowDelete={
          isEditable &&
          hasPermission("quartilesRatings.list.delete") &&
          handleRowDelete
        }
        onViewDetails={!isEditable && handleRowView}
      />

      <Modal
        title={
          isAdd
            ? t("editorGR.add_gr_indicators")
            : t("editorGR.edit_gr_indicators")
        }
        open={isAdd || isEdit || isView}
        onCancel={handleCloseModal}
        closable
        centered
        okButton={{
          htmlType: "submit",
          form: formId,
          children: t("ui.save"),
        }}
      >
        {(isAdd || isEdit || isView) && (
          <QRatingForm
            setCloseModal={handleCloseModal}
            refetch={refetch}
            prevElemId={isAdd ? prevElemId : ""}
            QRrating={!isAdd && selectedQ}
            isEditable={isEditable}
            formId={formId}
          />
        )}
      </Modal>

      <ConfirmModal
        title={t("indicators.indicator_del")}
        open={openModal === "Sure"}
        onConfirm={deleteListRating}
        onCancel={handleCloseModal}
        centered
        closable
      />
    </Wrapper.Content>
  );
};
export default EditorQIndicators;
